import { type IApiResponseError } from "@/shared/interfaces/api-response.interface";

/**
 * Parses a failed HTTP response body (JSON or text) into IApiResponseError.
 * Shared by report export and sample-data download fetchers.
 */
export function parseApiError(response: Response, body: unknown): IApiResponseError {
  const isApiErrorShape = (
    value: unknown,
  ): value is Pick<IApiResponseError, "error" | "statusCode" | "message" | "path" | "timestamp"> =>
    typeof value === "object" &&
    value !== null &&
    "statusCode" in value &&
    "message" in value;

  if (isApiErrorShape(body)) {
    return {
      error: body.error ?? response.statusText,
      statusCode: body.statusCode,
      message: body.message,
      path: body.path ?? response.url,
      timestamp: body.timestamp ?? new Date().toISOString(),
    };
  }

  const message =
    typeof body === "object" &&
    body !== null &&
    "message" in body &&
    typeof (body as { message: unknown }).message === "string"
      ? (body as { message: string }).message
      : response.statusText || "Unknown error";

  return {
    error: response.statusText,
    statusCode: response.status,
    message,
    path: response.url,
    timestamp: new Date().toISOString(),
  };
}

/**
 * Reads the body of a failed response (JSON or text) for use with parseApiError.
 */
export async function readErrorResponseBody(response: Response): Promise<unknown> {
  const contentType = response.headers.get("Content-Type") ?? "";
  try {
    if (contentType.includes("application/json")) {
      return await response.json();
    }
    return { message: (await response.text()) || response.statusText };
  } catch {
    return { message: response.statusText };
  }
}

/**
 * Extracts download filename from Content-Disposition header or returns fallback.
 */
export function getFilenameFromContentDisposition(
  contentDisposition: string | null,
  fallback: string,
): string {
  const filenameMatch = contentDisposition?.match(/filename="?([^";\n]+)"?/);
  const parsed = filenameMatch?.[1]?.trim();
  return parsed ?? fallback;
}

/**
 * Builds IApiResponseError for a fetch failure (network error, etc.).
 */
export function buildFetchError(url: string, err: unknown): IApiResponseError {
  const message = err instanceof Error ? err.message : "Network or request error";
  return {
    error: "Fetch failed",
    statusCode: 0,
    message,
    path: url,
    timestamp: new Date().toISOString(),
  };
}

export interface IFetchFileFromUrlResult {
  data?: { blob: Blob; filename: string };
  error?: IApiResponseError;
}

/**
 * Fetches a file from a URL and returns blob + filename for download.
 * Reuses the same pattern as report export: parse errors, Content-Disposition, fallback filename.
 * Use for Data page (signed downloadUrl) and Files/Drive (signed view URL).
 */
export async function fetchFileFromUrl(
  url: string,
  fallbackFilename: string,
  init?: RequestInit,
): Promise<IFetchFileFromUrlResult> {
  try {
    const response = await fetch(url, {
      method: "GET",
      ...init,
    });

    if (!response.ok) {
      const errorBody = await readErrorResponseBody(response);
      return { error: parseApiError(response, errorBody) };
    }

    const arrayBuffer = await response.arrayBuffer();
    const contentType = response.headers.get("Content-Type") ?? "application/octet-stream";
    const blob = new Blob([arrayBuffer], { type: contentType });
    const contentDisposition = response.headers.get("Content-Disposition");
    const filename = getFilenameFromContentDisposition(contentDisposition, fallbackFilename);
    return { data: { blob, filename } };
  } catch (err) {
    return { error: buildFetchError(url, err) };
  }
}
